import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import PropertyForm from './components/PropertyForm';
import TenantForm from './components/TenantForm';
import TransactionForm from './components/TransactionForm';
import MaintenanceForm from './components/MaintenanceForm';
import LoginForm from './components/LoginForm';
import PropertyCard from './components/PropertyCard';
import InvoiceForm from './components/InvoiceForm';
import Dashboard from './components/Dashboard';
import ReportsDashboard from './components/ReportsDashboard';
import MaintenanceLayout from './components/Maintenance/MaintenanceLayout';
import InvoiceReceipt from './components/InvoiceReceipt';
import CollectionNotice from './components/CollectionNotice';
import EmployeeForm from './components/EmployeeForm';
import PaymentDialog from './components/PaymentDialog';
import Settings from './components/Settings';
import Notifications from './components/Notifications';
import UserForm from './components/UserForm';
import TransactionReceipt from './components/TransactionReceipt';
import { Plus, Loader2, Database, MessageCircle, Wallet, AlertCircle, TrendingDown, TrendingUp, DollarSign, CreditCard, Search, ChevronRight, ChevronLeft, Filter, Calendar, Zap, Printer, Edit, Trash2, Users, Bell, ShieldCheck, Lock, Unlock, Menu, X, Eye, Tag, XCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Property, Tenant, Invoice, Transaction, MaintenanceRequest, Employee, SalaryPayment, AppUser, TransactionCategory, CompanyInfo } from './types';
import { api, API_URL } from './services/api';
import { getCollectionNoticeHtml } from './utils/printTemplates';
import { printContent } from './utils/print';
import TenantDetailsDialog from './components/TenantDetailsDialog';
import CategoryManagerDialog from './components/CategoryManagerDialog';
import ActivationScreen from './components/ActivationScreen';

const App: React.FC = () => {
  const [customAlert, setCustomAlert] = useState<{ message: string; visible: boolean }>({ message: '', visible: false });
  const [customConfirm, setCustomConfirm] = useState<{
    visible: boolean;
    message: string;
    resolve: (value: boolean) => void;
  }>({ visible: false, message: '', resolve: () => {} });

  const [customPrompt, setCustomPrompt] = useState<{
    visible: boolean;
    message: string;
    defaultValue: string;
    resolve: (value: string | null) => void;
  }>({ visible: false, message: '', defaultValue: '', resolve: () => {} });

  const [promptValue, setPromptValue] = useState('');

  useEffect(() => {
    if (customPrompt.visible) {
      setPromptValue(customPrompt.defaultValue);
    }
  }, [customPrompt.visible, customPrompt.defaultValue]);

  useEffect(() => {
    const handleAlert = (e: Event) => {
      const customEvent = e as CustomEvent;
      setCustomAlert({ message: customEvent.detail, visible: true });
    };
    window.addEventListener('app-alert', handleAlert);
    
    // Override window.alert
    window.alert = (message: any) => {
      const event = new CustomEvent('app-alert', { detail: String(message) });
      window.dispatchEvent(event);
    };

    // Override window.confirm
    window.confirm = (message?: string): any => {
      return new Promise<boolean>((resolve) => {
        setCustomConfirm({
          visible: true,
          message: message || '',
          resolve: (val) => {
            setCustomConfirm(prev => ({ ...prev, visible: false }));
            resolve(val);
          }
        });
      });
    };

    // Override window.prompt
    window.prompt = (message?: string, defaultValue?: string): any => {
      return new Promise<string | null>((resolve) => {
        setCustomPrompt({
          visible: true,
          message: message || '',
          defaultValue: defaultValue || '',
          resolve: (val) => {
            setCustomPrompt(prev => ({ ...prev, visible: false }));
            resolve(val);
          }
        });
      });
    };

    return () => {
      window.removeEventListener('app-alert', handleAlert);
    };
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check localStorage for existing auth
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'edit'>('list');
  const [modalType, setModalType] = useState<'none' | 'maintenance'>('none');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedTenantForDetails, setSelectedTenantForDetails] = useState<Tenant | null>(null);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<Invoice | null>(null);
  const [selectedInvoiceForReceipt, setSelectedInvoiceForReceipt] = useState<Invoice | null>(null);
  const [selectedInvoiceForDetails, setSelectedInvoiceForDetails] = useState<Invoice | null>(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  
  // Data States
  const [properties, setProperties] = useState<Property[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [salaries, setSalaries] = useState<SalaryPayment[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [transactionCategories, setTransactionCategories] = useState<TransactionCategory[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isLicensed, setIsLicensed] = useState<boolean | null>(null);
  
  // Check License Status on Mount
  useEffect(() => {
    const checkLicense = async () => {
      try {
        const response = await fetch(`${API_URL}/license/status`);
        if (response.ok) {
          const data = await response.json();
          setIsLicensed(data.isLicensed);
        } else {
          setIsLicensed(false);
        }
      } catch (e) {
        // If server is unreachable, we assume it's unlicensed for safety or loading
        setIsLicensed(false);
      }
    };
    checkLicense();
  }, []);

  const [isSaving, setIsSaving] = useState(false);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(localStorage.getItem('sidebarCollapsed') === 'true');
  const [currentUser, setCurrentUser] = useState<any>(JSON.parse(localStorage.getItem('currentUser') || 'null'));
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Invoices Filtering & Pagination States
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<'All' | 'Paid' | 'Pending' | 'Late'>('All');
  const [invoicePropertyFilter, setInvoicePropertyFilter] = useState<string>('All');
  const [invoiceMonthYearFilter, setInvoiceMonthYearFilter] = useState<string>(''); // YYYY-MM
  const [invoicePage, setInvoicePage] = useState(1);
  const [tenantPage, setTenantPage] = useState(1);
  const [transactionPage, setTransactionPage] = useState(1);
  const [maintenancePage, setMaintenancePage] = useState(1);
  const [employeePage, setEmployeePage] = useState(1);
  const [salaryPage, setSalaryPage] = useState(1);
  const [activeEmployeeTab, setActiveEmployeeTab] = useState<'employees' | 'salaries'>('employees');
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [selectedTransactionForPrint, setSelectedTransactionForPrint] = useState<Transaction | null>(null);
  
  const [tenantContractFilter, setTenantContractFilter] = useState<'All' | 'Active' | 'EndingSoon' | 'Expired'>('All');
  const PAGE_SIZE = 10;

  // Auth check
  const handleLogin = async (username: string, password: string) => {
    try {
        const user = await api.auth.login(username, password);
        if (user) {
            setIsAuthenticated(true);
            setCurrentUser(user);
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('currentUser', JSON.stringify(user));
        }
    } catch (e: any) {
        throw e;
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');
    window.location.reload();
  };

  const refreshAllData = useCallback(async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    try {
      const [props, tens, invs, exps, mains, emps, sals, categories, userList, companyInfoData] = await Promise.all([
        api.properties.getAll(),
        api.tenants.getAll(),
        api.invoices.getAll(),
        api.transactions.getAll(),
        api.maintenance.getAll(),
        api.employees.getAll(),
        api.salaries.getAll(),
        api.transactionCategories.getAll(),
        currentUser?.permissions?.userManagement !== false ? api.users.getAll() : Promise.resolve([]),
        api.companyInfo.get().catch(() => null)
      ]);
      setProperties(props);
      setTenants(tens);
      setInvoices(invs);
      setTransactions(exps);
      setMaintenance(mains);
      setEmployees(emps);
      setSalaries(sals);
      setTransactionCategories(categories);
      setUsers(userList);
      setCompanyInfo(companyInfoData);
      if (companyInfoData) {
        localStorage.setItem('companyInfo', JSON.stringify(companyInfoData));
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isAuthenticated && isLicensed === true) {
      refreshAllData(true);
      // Auto-refresh every 10 seconds in the background
      const interval = setInterval(() => {
        refreshAllData(false);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, isLicensed, refreshAllData]);

  // Silently refresh data on any tab change to ensure UI is up-to-date
  useEffect(() => {
    if (isAuthenticated && isLicensed === true) {
      refreshAllData(false);
    }
  }, [activeTab, isAuthenticated, isLicensed, refreshAllData]);

  // Track screen size and collapse sidebar automatically on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarCollapsed(true);
      }
    };
    
    // Initial check on mount
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Add a Header component or just insert it in the main render
  // For now, let's add it to the top right of the main area if not dashboard (dashboard has its own header usually, but here App.tsx handles layout)
  // Actually, checking renderHeader in previous views...
  
  // Let's modify the Main layout to include a Top Bar


  // Handlers
  const handlePropertySubmit = async (data: Property) => {
    setIsSaving(true);
    try {
      if (selectedProperty) {
        await api.properties.update(selectedProperty.id, data);
        alert("✅ تم تحديث العقار والوحدات بنجاح");
      } else {
        await api.properties.create(data);
        alert("✅ تم إضافة العقار والوحدات بنجاح");
      }
      // Refetch all properties to get the saved units with their database IDs
      const updatedProperties = await api.properties.getAll();
      setProperties(updatedProperties);
      setViewMode('list');
      setSelectedProperty(null);
    } catch (error: any) { 
      alert("❌ خطأ: " + (error.message || 'فشل في العملية'));
    } finally { setIsSaving(false); }
  };

  const handleTenantSubmit = async (data: any) => {
    setIsSaving(true);
    try {
      const isExistingDbTenant = selectedTenant || tenants.some(t => String(t.id) === String(data.id));
      if (isExistingDbTenant) {
        await api.tenants.update(data.id, data);
        alert("✅ تم تعديل بيانات المستأجر بنجاح");
      } else {
        const newTenant = await api.tenants.create(data);
        if (data.pendingAttachments && data.pendingAttachments.length > 0) {
            const tenantId = newTenant.id;
            await Promise.all(data.pendingAttachments.map((file: any) => 
                api.attachments.upload({
                    entity_type: 'tenant',
                    entity_id: String(tenantId),
                    file_name: file.name,
                    file_type: file.type,
                    file_data: file.data
                })
            ));
        }
        if (newTenant.warning) {
          alert("⚠️ تم تسجيل المستأجر ولكن: " + newTenant.warning);
        } else {
          alert("✅ تم تسجيل المستأجر بنجاح");
        }
      }
      
      const [updatedTenants, updatedProperties] = await Promise.all([
        api.tenants.getAll(),
        api.properties.getAll()
      ]);
      setTenants(updatedTenants);
      setProperties(updatedProperties);

      setViewMode('list');
      setSelectedTenant(null);
      setSelectedPropertyId(null);
    } catch (error: any) { 
      alert("❌ خطأ: " + (error.message || 'فشل في عملية المستأجر'));
    } finally { setIsSaving(false); }
  };

  const handleTransactionSubmit = async (data: Transaction) => {
    setIsSaving(true);
    try {
      const newTransaction = await api.transactions.create(data);
      setTransactions(prev => [...prev, newTransaction]);
      setViewMode('list');
      alert("✅ تم تسجيل المعاملة بنجاح");
    } catch (error: any) { 
      alert("❌ خطأ: " + (error.message || 'فشل في تسجيل المعاملة'));
    } finally { setIsSaving(false); }
  };

  const handleMaintenanceSubmit = async (data: MaintenanceRequest) => {
      setIsSaving(true);
      try {
        const newMain = await api.maintenance.create(data);
        setMaintenance(prev => [...prev, newMain]);
        setModalType('none');
        alert("✅ تم تسجيل طلب الصيانة بنجاح");
      } catch (error: any) {
        alert("❌ خطأ: " + (error.message || 'فشل في تسجيل الطلب'));
      } finally { setIsSaving(false); }
  };

  const handleEmployeeSubmit = async (data: any) => {
      setIsSaving(true);
      try {
        if (data.id) {
            await api.employees.update(data.id, data);
            alert("✅ تم تعديل بيانات الموظف بنجاح");
        } else {
            await api.employees.create(data);
            alert("✅ تم تسجيل الموظف بنجاح");
        }
        const updated = await api.employees.getAll();
        setEmployees(updated);
        setShowEmployeeForm(false);
      } catch (error: any) {
        alert("❌ خطأ: " + (error.message || 'فشل في العملية'));
      } finally { setIsSaving(false); }
  };
  
  const handleUserSubmit = async (data: any) => {
    setIsSaving(true);
    try {
        if (data.id) {
            await api.users.update(data.id, data);
            alert("✅ تم تعديل بيانات المستخدم بنجاح");
        } else {
            await api.users.create(data);
            alert("✅ تم إنشاء المستخدم بنجاح");
        }
        const updated = await api.users.getAll();
        setUsers(updated);
        setShowUserForm(false);
    } catch (error: any) {
        alert("❌ خطأ: " + (error.message || 'فشل في العملية'));
    } finally { setIsSaving(false); }
  };

  const handleDeleteUser = async (id: string) => {
    if (!await window.confirm("⚠️ هل أنت متأكد من أرشفة هذا المستخدم؟ سيتم منعه من الدخول ولكن ستبقى بياناته مسجلة للتتبع.")) return;
    try {
        await api.users.delete(id);
        setUsers(users.filter(u => u.id !== id));
        alert("✅ تم أرشفة المستخدم بنجاح");
    } catch (e: any) {
        alert("❌ فشل الأرشفة: " + e.message);
    }
  };

  const handleToggleUserStatus = async (user: AppUser) => {
    const newStatus = user.status === 'Active' ? 'Disabled' : 'Active';
    try {
        await api.users.update(user.id, { ...user, status: newStatus });
        setUsers(users.map(u => u.id === user.id ? { ...u, status: newStatus } : u));
        alert(`✅ تم ${newStatus === 'Active' ? 'تنشيط' : 'تعطيل'} الحساب بنجاح`);
    } catch (e: any) {
        alert("❌ فشل تغيير الحالة: " + e.message);
    }
  };

  const handleSalarySubmit = async (data: any) => {
      const duplicate = salaries.some(s => String(s.employeeId) === String(data.employeeId) && s.period === data.period);
      if (duplicate) {
          alert(`⚠️ لا يمكن صرف الراتب! تم تسجيل مسير رواتب لهذا الموظف في دورة ${data.period} مسبقاً.`);
          return;
      }
      setIsSaving(true);
      try {
        const newSal = await api.salaries.create(data);
        setSalaries(prev => [newSal, ...prev]);
        const updatedTxs = await api.transactions.getAll();
        setTransactions(updatedTxs);
        alert("✅ تم صرف الراتب بنجاح");
      } catch (error: any) {
        alert("❌ خطأ: " + (error.message || 'فشل في صرف الراتب'));
      } finally { setIsSaving(false); }
  };
  const handleBulkGenerateInvoices = async () => {
    if (!await window.confirm("هل أنت متأكد من رغبتك في توليد فواتير لكافة الوحدات المؤجرة لهذا الشهر؟ سيتجاهل النظام الوحدات التي تم إصدار فاتورة لها مسبقاً.")) return;
    
    setIsSaving(true);
    const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM
    const today = new Date().toISOString().split('T')[0];
    let createdCount = 0;

    try {
        for (const prop of properties) {
            if (!prop.units) continue;
            for (const unit of prop.units) {
                // If unit is occupied (has tenantId)
                if (unit.tenantId) {
                    // Check if invoice already exists for this tenant, unit, and month
                    const exists = invoices.some(inv => 
                        String(inv.tenantId) === String(unit.tenantId) && 
                        String(inv.unitId) === String(unit.id) && 
                        inv.monthDue === currentMonth
                    );

                    if (!exists) {
                        await api.invoices.create({
                            tenantId: unit.tenantId,
                            unitId: unit.id,
                            amount: unit.rent,
                            dueDate: today,
                            monthDue: currentMonth,
                            status: 'Pending'
                        });
                        createdCount++;
                    }
                }
            }
        }
        
        if (createdCount > 0) {
            // Refetch invoices
            const updated = await api.invoices.getAll();
            setInvoices(updated);
            alert(`✅ تم توليد ${createdCount} فاتورة جديدة بنجاح`);
        } else {
            alert("ℹ️ كافة الوحدات المؤجرة لديها فواتير لهذا الشهر بالفعل");
        }
    } catch (e) {
        console.error("Bulk generation failed", e);
        alert("❌ حدث خطأ أثناء التوليد الجماعي");
    } finally {
        setIsSaving(false);
    }
  };

  const handleInvoiceSubmit = async (data: any) => {
      setIsSaving(true);
      try {
          const { linkedExpenseIds, ...invoiceData } = data;
          const newInv = await api.invoices.create(invoiceData);
          
          // Link expenses if any
          if (linkedExpenseIds && linkedExpenseIds.length > 0) {
              await Promise.all(linkedExpenseIds.map((id: string) => 
                  api.transactions.linkToInvoice(id, newInv.id)
              ));
          }

          setInvoices(prev => [...prev, newInv]);
          // Refresh transactions to show they are now linked
          const updatedTransactions = await api.transactions.getAll();
          setTransactions(updatedTransactions);

          setShowInvoiceForm(false);
          alert("✅ تم إصدار الفاتورة وربط المصروفات بنجاح");
      } catch (error: any) { 
          alert("❌ خطأ: " + (error.message || 'فشل في إصدار الفاتورة'));
      } finally { setIsSaving(false); }
  };

  const handlePayInvoice = async (id: string, paymentData?: any) => {
      try {
          await api.invoices.pay(id, paymentData);
          // Refresh invoices and transactions
          const [updatedInvs, updatedTxs] = await Promise.all([
              api.invoices.getAll(),
              api.transactions.getAll()
          ]);
          setInvoices(updatedInvs);
          setTransactions(updatedTxs);
          
          // Find the paid invoice in updated list to show receipt
          const paidInv = updatedInvs.find(inv => String(inv.id) === String(id));
          if (paidInv) {
              setSelectedInvoiceForDetails(paidInv);
          }
          
          setSelectedInvoiceForPayment(null); // Close payment dialog
          // alert("✅ تم تسجيل عملية الدفع بنجاح"); // Removed alert to show receipt immediately
      } catch (error: any) {
          alert("❌ فشل التسديد: " + error.message);
      }
  };

  // Render Functions
  const renderHeader = (title: string, buttonText?: string, onButtonClick?: () => void) => (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
           {title}
           {isLoading && <Loader2 className="w-5 h-5 animate-spin text-fly-500" />}
        </h1>
        <p className="text-slate-500 mt-1 flex items-center gap-1">
          <Database className="w-3 h-3 text-fly-600" />
          <span className="text-xs font-medium text-fly-600">نظام إدارة العقارات</span>
        </p>
      </div>
      {buttonText && !isLoading && (
        <button 
          onClick={onButtonClick || (() => setViewMode('create'))}
          className="flex items-center space-x-2 space-x-reverse bg-fly-600 hover:bg-fly-700 text-white px-5 py-2.5 rounded-xl shadow-lg shadow-fly-600/20 transition-all transform hover:-translate-y-0.5"
        >
          <Plus className="w-5 h-5" />
          <span>{buttonText}</span>
        </button>
      )}
    </div>
  );

  const renderPagination = (currentPage: number, totalItems: number, setter: React.Dispatch<React.SetStateAction<number>>, itemName: string = "عنصر", theme: 'light' | 'dark' = 'light') => {
      const totalPages = Math.ceil(totalItems / PAGE_SIZE);
      if (totalPages <= 1) return null;
      
      const isDark = theme === 'dark';
      return (
        <div className={`p-4 border-t flex items-center justify-between ${isDark ? 'bg-slate-800/50 border-white/5' : 'bg-slate-50 border-slate-100'}`}>
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                عرض {(currentPage - 1) * PAGE_SIZE + 1} إلى {Math.min(currentPage * PAGE_SIZE, totalItems)} من أصل {totalItems} {itemName}
            </p>
            <div className="flex gap-1 flex-wrap">
                <button 
                    onClick={() => setter(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`p-2 border rounded-lg transition-all disabled:opacity-30 ${isDark ? 'bg-slate-900 border-white/10 text-slate-400 hover:bg-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                    <ChevronRight size={16} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setter(i + 1)}
                        className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                            currentPage === i + 1 
                            ? 'bg-fly-600 text-white shadow-lg shadow-fly-600/20' 
                            : (isDark ? 'bg-slate-900 text-slate-400 border border-white/10 hover:bg-slate-800' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50')
                        }`}
                    >
                        {i + 1}
                    </button>
                ))}
                <button 
                    onClick={() => setter(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={`p-2 border rounded-lg transition-all disabled:opacity-30 ${isDark ? 'bg-slate-900 border-white/10 text-slate-400 hover:bg-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                    <ChevronLeft size={16} />
                </button>
            </div>
        </div>
      );
  };

  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center h-64 text-slate-400">
      <Loader2 className="w-10 h-10 animate-spin mb-4 text-fly-600" />
      <p>جاري تحميل البيانات من السيرفر...</p>
    </div>
  );

  const renderProperties = () => {
    if (viewMode === 'create' || viewMode === 'edit') {
      return (
        <div className="relative">
          {isSaving && <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-sm rounded-2xl"><Loader2 className="w-8 h-8 animate-spin text-fly-600" /></div>}
          <PropertyForm 
            onCancel={() => { setViewMode('list'); setSelectedProperty(null); }} 
            onSubmit={handlePropertySubmit}
            initialData={selectedProperty} 
          />
        </div>
      );
    }
    if (isLoading) return renderLoading();

    return (
      <div className="space-y-6">
        {renderHeader('العقارات', 'إضافة عقار جديد')}
        <div className="space-y-6">
          {properties.map(property => (
            <PropertyCard 
                key={property.id} 
                property={property} 
                invoices={invoices}
                onAddTenant={(propertyId) => { 
                  setSelectedPropertyId(propertyId); 
                  setActiveTab('tenants'); 
                  setViewMode('create'); 
                }}
                onAddExpense={() => { setActiveTab('expenses'); setViewMode('create'); }}
                onAddUnit={(id) => {
                  const property = properties.find(p => p.id === id);
                  if (property) {
                    setSelectedProperty(property);
                    setViewMode('edit');
                  }
                }}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderTenants = () => {
    if (viewMode === 'create' || viewMode === 'edit') {
        return (
          <div className="relative">
            {isSaving && <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-sm rounded-2xl"><Loader2 className="w-8 h-8 animate-spin text-fly-600" /></div>}
            <TenantForm 
              onCancel={() => { setViewMode('list'); setSelectedPropertyId(null); setSelectedTenant(null); }} 
              onSubmit={handleTenantSubmit} 
              preSelectedPropertyId={selectedPropertyId}
              initialData={selectedTenant}
              existingTenants={tenants}
            />
          </div>
        );
      }
      if (isLoading) return renderLoading();

      const filteredTenants = tenants.filter(t => {
          const searchMatch = !searchTerm || 
              `${t.firstName} ${t.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (t.mobile && t.mobile.includes(searchTerm)) ||
              (t.property_name && t.property_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
              (t.unit_number && t.unit_number.toLowerCase().includes(searchTerm.toLowerCase()));

          if (!searchMatch) return false;

          if (tenantContractFilter === 'All') return true;
          
          if (!t.contractEnd) return tenantContractFilter === 'Active';
          
          const endDate = new Date(t.contractEnd);
          const today = new Date();
          const msPerDay = 1000 * 60 * 60 * 24;
          const diffDays = (endDate.getTime() - today.getTime()) / msPerDay;
          
          if (tenantContractFilter === 'Expired') return diffDays < 0;
          if (tenantContractFilter === 'EndingSoon') return diffDays >= 0 && diffDays <= 30;
          if (tenantContractFilter === 'Active') return diffDays >= 0; 
          
          return true;
      });

      return (
        <div className="space-y-6">
          {renderHeader('المستأجرين', 'تسجيل مستأجر', () => {
              setSelectedPropertyId(null);
              setViewMode('create');
          })}

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                      <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input 
                          type="text" 
                          placeholder="البحث باسم المستأجر، الهاتف، العقار..." 
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl py-2.5 pr-12 pl-4 outline-none focus:border-fly-500 transition-all text-sm"
                      />
                  </div>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 sm:gap-4 border-t border-slate-100 pt-4">
                  <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-100 overflow-x-auto max-w-full hide-scrollbar">
                      {(['All', 'Active', 'EndingSoon', 'Expired'] as const).map(status => (
                          <button
                              key={status}
                              onClick={() => setTenantContractFilter(status)}
                              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                                  tenantContractFilter === status 
                                  ? 'bg-white text-fly-600 shadow-sm ring-1 ring-slate-200' 
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                          >
                              {status === 'All' ? 'الكل' : 
                               status === 'Active' ? 'سارية' : 
                               status === 'EndingSoon' ? 'تنبيه: تنتهي خلال 30 يوم' : 'منتهية'}
                          </button>
                      ))}
                  </div>
              </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
             <div className="overflow-x-auto">
                 <table className="w-full text-right">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold text-sm">
                        <tr>
                            <th className="p-4 text-right">الاسم</th>
                            <th className="p-4 text-center hidden md:table-cell">نوع المستأجر</th>
                            <th className="p-4 text-right">العقار والوحدة</th>
                            <th className="p-4 text-center hidden sm:table-cell">رقم الهاتف</th>
                            <th className="p-4 text-center hidden lg:table-cell">الدورة</th>
                            <th className="p-4 text-center hidden md:table-cell">تاريخ انتهاء العقد</th>
                            <th className="p-4 text-center">إجراءات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {filteredTenants.slice((tenantPage - 1) * PAGE_SIZE, tenantPage * PAGE_SIZE).map(t => (
                            <tr key={t.id} className="hover:bg-slate-50">
                                <td className="p-4 font-bold text-slate-800 text-right">{t.firstName} {t.lastName}</td>
                                <td className="p-4 text-slate-600 text-center hidden md:table-cell">{t.type === 'Individual' ? 'فرد' : 'شركة'}</td>
                                <td className="p-4 text-slate-600 text-right">
                                    {t.assignedUnits && t.assignedUnits.length > 0 ? (
                                        <div className="space-y-1">
                                            {t.assignedUnits.map((u: any, i: number) => (
                                                <div key={i} className="inline-block bg-slate-100 px-2 py-1 rounded text-xs font-bold text-slate-800 mr-1 mb-1">
                                                    {u.propertyName} - {u.unitNumber}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-slate-400 italic">غير مسكن</span>
                                    )}
                                </td>
                                <td className="p-4 text-slate-600" dir="ltr">{t.mobile}</td>
                                <td className="p-4 text-slate-600">{t.paymentCycle}</td>
                                <td className="p-4 text-slate-600 font-mono text-sm">{t.contractEnd ? new Date(t.contractEnd).toLocaleDateString('en-GB') : '-'}</td>
                                <td className="p-4 text-center">
                                    <button 
                                        onClick={() => {
                                            const tenantAssigned: any[] = [];
                                            properties.forEach(p => {
                                                p.units.forEach(u => {
                                                    if (String(u.tenantId) === String(t.id)) {
                                                        tenantAssigned.push({
                                                            propertyId: String(p.id),
                                                            propertyName: p.name,
                                                            unitId: String(u.id),
                                                            unitNumber: u.unitNumber,
                                                            rent: u.contractRent || u.rent
                                                        });
                                                    }
                                                });
                                            });
                                            setSelectedTenantForDetails({ ...t, assignedUnits: tenantAssigned });
                                        }}
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-500 hover:bg-fly-100 hover:text-fly-600 transition-colors ml-2"
                                        title="عرض تفاصيل المستأجر ومرفقاته"
                                    >
                                        <Eye size={16} />
                                    </button>
                                    <button 
                                        onClick={() => {
                                            // Assign populated assignedUnits from properties for the tenant editor
                                            const tenantAssigned: any[] = [];
                                            properties.forEach(p => {
                                                p.units.forEach(u => {
                                                    if (String(u.tenantId) === String(t.id)) {
                                                        tenantAssigned.push({
                                                            propertyId: String(p.id),
                                                            unitId: String(u.id),
                                                            rent: u.contractRent || u.rent
                                                        });
                                                    }
                                                });
                                            });
                                            setSelectedTenant({ ...t, assignedUnits: tenantAssigned });
                                            setViewMode('edit');
                                        }}
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-500 hover:bg-fly-100 hover:text-fly-600 transition-colors"
                                        title="تعديل بيانات المستأجر"
                                    >
                                        <Edit size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                 </table>
                 {tenants.length === 0 && <div className="p-10 text-center text-slate-400">لا يوجد مستأجرين حالياً</div>}
             </div>
             {renderPagination(tenantPage, filteredTenants.length, setTenantPage, "مستأجر")}
          </div>
        </div>
      );
  };

  const renderInvoices = () => {
      if (isLoading) return renderLoading();

      // Filtering Logic
      const filteredInvoices = invoices.filter(inv => {
          const matchesSearch = (inv.tenantName || '').toLowerCase().includes(invoiceSearch.toLowerCase()) || 
                               (inv.propertyName || '').toLowerCase().includes(invoiceSearch.toLowerCase()) ||
                               String(inv.unitNumber || '').toLowerCase().includes(invoiceSearch.toLowerCase());
          const matchesStatus = invoiceStatusFilter === 'All' || inv.status === invoiceStatusFilter;
          const matchesProperty = invoicePropertyFilter === 'All' || String(inv.propertyId) === String(invoicePropertyFilter);
          const matchesMonth = !invoiceMonthYearFilter || inv.monthDue === invoiceMonthYearFilter;
          
          return matchesSearch && matchesStatus && matchesProperty && matchesMonth;
      }).sort((a, b) => Number(b.id) - Number(a.id));

      // Pagination Logic
      const paginatedInvoices = filteredInvoices.slice(
          (invoicePage - 1) * PAGE_SIZE,
          invoicePage * PAGE_SIZE
      );

      // Stats should reflect filtered data for better insights
      const statsInvoices = filteredInvoices;
      const totalInvoicesAmount = statsInvoices.reduce((sum, inv) => sum + Number(inv.amount || 0), 0);
      const totalPaid = statsInvoices.filter(inv => inv.status === 'Paid').reduce((sum, inv) => sum + Number(inv.amount || 0), 0);
      const totalArrears = statsInvoices.filter(inv => inv.status !== 'Paid').reduce((sum, inv) => sum + Number(inv.amount || 0), 0);

      const handleWhatsAppAlert = (invoice: Invoice) => {
        const tenant = tenants.find(t => t.id === invoice.tenantId);
        if (!tenant || !tenant.mobile) {
          alert('⚠️ لا يوجد رقم هاتف متاح لهذا المستأجر');
          return;
        }
        
        // Clean the number and ensure country code
        let cleanNumber = tenant.mobile.replace(/\D/g, ''); // Keep only digits
        if (cleanNumber.length === 8) {
          cleanNumber = '965' + cleanNumber;
        } else if (cleanNumber.startsWith('00')) {
          cleanNumber = cleanNumber.substring(2);
        } else if (cleanNumber.startsWith('0')) {
          cleanNumber = '965' + cleanNumber.substring(1);
        }

        const message = `تذكير من إدارة العقارات: يرجى العلم بأن فاتورة إيجار شهر ${invoice.monthDue} لعقار ${invoice.propertyName} - وحدة ${invoice.unitNumber} بقيمة ${invoice.amount} د.ك مستحقة للسداد. شكراً لتعاونكم.`;
        const encodedMsg = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodedMsg}`;
        window.open(whatsappUrl, '_blank');
      };

      const handleBulkPrintClaims = async () => {
          const unpaid = filteredInvoices.filter(inv => inv.status !== 'Paid');
          if (unpaid.length === 0) {
              alert('⚠️ لا توجد أي مطالبات غير مسددة لطباعتها في القائمة الحالية المصفاة.');
              return;
          }
          
          if (!await window.confirm(`هل أنت متأكد من طباعة عدد (${unpaid.length}) مطالبة مالية لكافة المستأجرين المعروضين؟`)) {
              return;
          }

          const htmls = unpaid.map(inv => {
              const linked = transactions.filter(e => e.invoiceId === inv.id && e.type === 'Expense');
              return `<div class="print-page-break">${getCollectionNoticeHtml(inv, companyInfo, linked, false)}</div>`;
          });

          const combinedHtml = htmls.join('');
          printContent('طباعة مطالبات جماعية', combinedHtml);
      };

      return (
          <div className="space-y-6">
              <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-6">
                <div className="flex gap-4">
                  <button 
                    onClick={() => setShowInvoiceForm(true)}
                    className="flex items-center gap-2 bg-fly-600 hover:bg-fly-700 text-white px-5 py-2.5 rounded-xl shadow-lg shadow-fly-600/20 transition-all font-bold"
                  >
                    <Plus size={18} />
                    إصدار فاتورة
                  </button>
                  <button 
                    onClick={handleBulkGenerateInvoices}
                    disabled={isSaving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-5 py-2.5 rounded-xl shadow-lg transition-all font-bold group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Zap size={18} className="text-yellow-400 group-hover:scale-125 transition-transform" />
                    )}
                    {isSaving ? 'جاري التوليد...' : 'توليد فواتير الشهر الحالي'}
                  </button>
                  <button 
                    onClick={handleBulkPrintClaims}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl shadow-lg shadow-indigo-600/20 transition-all font-bold group"
                    title="طباعة كافة المطالبات المالية غير المسددة لجميع المستأجرين في القائمة المصفاة حالياً"
                  >
                    <Printer size={18} className="group-hover:scale-110 transition-transform" />
                    طباعة كل المطالبات
                  </button>
                </div>
                <h2 className="text-xl font-black text-slate-800 hidden md:block">الفواتير والتحصيل</h2>
              </div>
              
              {/* Stats Hub */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between group hover:border-fly-200 transition-all">
                    <div>
                        <p className="text-slate-500 text-xs font-bold mb-1">مجموع الفواتير</p>
                        <h3 className="text-2xl font-black text-slate-800">{totalInvoicesAmount.toLocaleString()} <span className="text-sm font-normal text-slate-400">KWD</span></h3>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl group-hover:bg-fly-50 transition-colors">
                        <Database className="w-6 h-6 text-slate-400 group-hover:text-fly-600" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between group hover:border-green-200 transition-all">
                    <div>
                        <p className="text-slate-500 text-xs font-bold mb-1">مجموع المدفوع</p>
                        <h3 className="text-2xl font-black text-slate-800 text-green-600">{totalPaid.toLocaleString()} <span className="text-sm font-normal text-slate-400 text-green-600/60">KWD</span></h3>
                    </div>
                    <div className="bg-green-50 p-4 rounded-2xl">
                        <Wallet className="w-6 h-6 text-green-600" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between group hover:border-red-200 transition-all">
                    <div>
                        <p className="text-slate-500 text-xs font-bold mb-1">مجموع الغير محصل</p>
                        <h3 className="text-2xl font-black text-slate-800 text-red-600">{totalArrears.toLocaleString()} <span className="text-sm font-normal text-slate-400 text-red-600/60">KWD</span></h3>
                    </div>
                    <div className="bg-red-50 p-4 rounded-2xl">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                </div>
              </div>

              {/* Filters Bar */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            type="text" 
                            placeholder="البحث باسم المستأجر، العقار، أو رقم الوحدة..." 
                            value={invoiceSearch}
                            onChange={(e) => { setInvoiceSearch(e.target.value); setInvoicePage(1); }}
                            className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl py-2.5 pr-12 pl-4 outline-none focus:border-fly-500 transition-all text-sm"
                        />
                    </div>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 sm:gap-4 border-t border-slate-100 pt-4">
                    <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-100 overflow-x-auto max-w-full hide-scrollbar">
                        {(['All', 'Pending', 'Paid', 'Late'] as const).map(status => (
                            <button
                                key={status}
                                onClick={() => { setInvoiceStatusFilter(status); setInvoicePage(1); }}
                                className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                                    invoiceStatusFilter === status 
                                    ? 'bg-white text-fly-600 shadow-sm ring-1 ring-slate-200' 
                                    : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                {status === 'All' ? 'الكل' : 
                                 status === 'Paid' ? 'تم السداد' : 
                                 status === 'Late' ? 'متأخرة' : 'بانتظار السداد'}
                            </button>
                        ))}
                    </div>

                    <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[200px]">
                        <select 
                            value={invoicePropertyFilter}
                            onChange={(e) => { setInvoicePropertyFilter(e.target.value); setInvoicePage(1); }}
                            className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl py-2 px-3 outline-none focus:border-fly-500 transition-all text-xs font-bold text-slate-600"
                        >
                            <option value="All">فلترة حسب العقار (الكل)</option>
                            {properties.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center justify-between sm:justify-start gap-2 bg-slate-50 border-2 border-slate-100 rounded-xl px-3 py-1.5 w-full sm:w-auto">
                        <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-slate-400" />
                            <input 
                                type="month" 
                                value={invoiceMonthYearFilter}
                                onChange={(e) => { setInvoiceMonthYearFilter(e.target.value); setInvoicePage(1); }}
                                className="bg-transparent text-xs font-bold text-slate-600 outline-none w-full sm:w-auto"
                            />
                        </div>
                        {invoiceMonthYearFilter && (
                            <button 
                                onClick={() => { setInvoiceMonthYearFilter(''); setInvoicePage(1); }}
                                className="text-slate-400 hover:text-red-500 transition-colors"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                      <table className="w-full text-right text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                          <tr>
                              <th className="p-4 text-center hidden sm:table-cell">رقم الفاتورة</th>
                              <th className="p-4 text-right">المستأجر</th>
                              <th className="p-4 text-center hidden lg:table-cell">كود الدورة</th>
                              <th className="p-4 text-center hidden md:table-cell">الشهر</th>
                              <th className="p-4 text-center">المبلغ</th>
                              <th className="p-4 text-center">الحالة</th>
                              <th className="p-4 text-center">الإجراءات</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                          {paginatedInvoices.map(inv => (
                               <tr key={inv.id} className="hover:bg-slate-50 transition-colors">
                                   <td className="p-4 font-mono text-slate-500 text-center hidden sm:table-cell">{inv.id}</td>
                                   <td className="p-4 font-medium text-right">
                                       <div className="flex flex-col">
                                           <span className="text-slate-900">{inv.tenantName}</span>
                                           <span className="text-[10px] text-slate-400 font-medium">{inv.propertyName} - {inv.unitNumber}</span>
                                       </div>
                                   </td>
                                   <td className="p-4 text-center hidden lg:table-cell">
                                       <span className="bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-md text-[10px] font-mono text-slate-600">
                                           CYC-{inv.monthDue.replace('-', '')}
                                       </span>
                                   </td>
                                   <td className="p-4 text-center font-medium text-slate-600 hidden md:table-cell">{inv.monthDue}</td>
                                   <td className="p-4 font-black text-slate-800 text-center">{Number(inv.amount || 0).toLocaleString()} <span className="text-[10px] font-normal text-slate-400">KWD</span></td>
                                   <td className="p-4 text-center">
                                       <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                                           inv.status === 'Paid' ? 'bg-green-100 text-green-700' : 
                                           inv.status === 'Late' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                       }`}>
                                           {inv.status === 'Paid' ? 'تم السداد' : inv.status === 'Late' ? 'متأخرة' : 'بانتظار السداد'}
                                       </span>
                                   </td>
                                   <td className="p-4 text-center">
                                        <div className="flex gap-1.5 justify-center items-center flex-wrap md:flex-nowrap">
                                            {inv.status !== 'Paid' && (
                                                <button 
                                                    onClick={() => setSelectedInvoiceForPayment(inv)}
                                                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl transition-all shadow-md font-bold text-xs hover:scale-105 active:scale-95 shrink-0"
                                                    title="تسجيل سداد الفاتورة وتحصيل مبلغ الإيجار"
                                                >
                                                    <CreditCard size={12} />
                                                    <span className="hidden sm:inline">سداد</span>
                                                </button>
                                            )}

                                            {inv.status !== 'Paid' && (
                                                <button 
                                                    onClick={() => handleWhatsAppAlert(inv)}
                                                    className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl transition-all shadow-md font-bold text-xs hover:scale-105 active:scale-95"
                                                    title="إرسال رسالة تذكير بالسداد للمستأجر عبر تطبيق واتساب"
                                                >
                                                    <MessageCircle size={12} />
                                                    <span className="hidden sm:inline">تنبيه</span>
                                                </button>
                                            )}
                                            <button 
                                                 onClick={() => setSelectedInvoiceForDetails(inv)}
                                                 className="flex items-center gap-1 bg-slate-100 text-slate-700 hover:bg-slate-200 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl transition-all font-bold text-xs hover:scale-105 active:scale-95" 
                                                 title={inv.status === 'Paid' ? "عرض تفاصيل الفاتورة المسددة" : "عرض وطباعة إشعار المطالبة المالية للمستأجر"}
                                            >
                                                 <Eye size={12} />
                                                 <span className="hidden sm:inline">{inv.status === 'Paid' ? "تفاصيل" : "مطالبة"}</span>
                                            </button>
                                        </div>
                                    </td>
                               </tr>
                           ))}
                      </tbody>
                  </table>
                </div>
                  
                  {filteredInvoices.length === 0 && (
                    <div className="p-20 text-center">
                        <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                            <Search size={32} />
                        </div>
                        <p className="text-slate-500 font-medium">لم يتم العثور على أي فواتير تطابق بحثك</p>
                    </div>
                  )}

                  {renderPagination(invoicePage, filteredInvoices.length, setInvoicePage, "فاتورة")}
              </div>
          </div>
      )
  };

  const renderTransactions = () => {
    if (viewMode === 'create') {
        return (
          <div className="relative">
            {isSaving && <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-sm rounded-2xl"><Loader2 className="w-8 h-8 animate-spin text-fly-600" /></div>}
            <TransactionForm onCancel={() => setViewMode('list')} onSubmit={handleTransactionSubmit} properties={properties} tenants={tenants} transactionCategories={transactionCategories} setTransactionCategories={setTransactionCategories} />
          </div>
        );
    }
    if (isLoading) return renderLoading();
    
    const sortedTransactions = [...transactions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const totalIncome = transactions.filter(t => t.type === 'Income').reduce((sum, t) => sum + Number(t.amount), 0);
    const totalExpense = transactions.filter(t => t.type === 'Expense').reduce((sum, t) => sum + Number(t.amount), 0);
    const netBalance = totalIncome - totalExpense;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                   سجل الإيرادات والمصروفات
                   {isLoading && <Loader2 className="w-5 h-5 animate-spin text-fly-500" />}
                </h1>
                <p className="text-slate-500 mt-1 flex items-center gap-1">
                  <Database className="w-3 h-3 text-fly-600" />
                  <span className="text-xs font-medium text-fly-600">نظام إدارة العقارات</span>
                </p>
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowCategoryManager(true)}
                  className="flex items-center gap-2 bg-slate-105 hover:bg-slate-200 text-slate-700 px-5 py-2.5 rounded-xl border border-slate-200 transition-all font-bold text-sm"
                >
                  <Tag className="w-4 h-4 text-slate-550" />
                  <span>إدارة التصنيفات</span>
                </button>
                <button 
                  onClick={() => setViewMode('create')}
                  className="flex items-center space-x-2 space-x-reverse bg-fly-600 hover:bg-fly-700 text-white px-5 py-2.5 rounded-xl shadow-lg shadow-fly-600/20 transition-all transform hover:-translate-y-0.5 text-sm font-bold"
                >
                  <Plus className="w-5 h-5" />
                  <span>إضافة معاملة (صرف/قبض)</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-slate-500 mb-1">إجمالي الإيرادات</p>
                        <p className="text-2xl font-black text-emerald-600">{totalIncome.toFixed(3)} د.ك</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500">
                        <TrendingUp size={24} />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-slate-500 mb-1">إجمالي المصروفات</p>
                        <p className="text-2xl font-black text-rose-600">{totalExpense.toFixed(3)} د.ك</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center text-rose-500">
                        <TrendingDown size={24} />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-slate-500 mb-1">صافي الرصيد</p>
                        <p className={`text-2xl font-black ${netBalance >= 0 ? 'text-fly-600' : 'text-rose-600'}`}>{netBalance.toFixed(3)} د.ك</p>
                    </div>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${netBalance >= 0 ? 'bg-fly-50 text-fly-500' : 'bg-rose-50 text-rose-500'}`}>
                        <DollarSign size={24} />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                     <table className="w-full text-right text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                          <tr>
                              <th className="p-4 text-right">التاريخ</th>
                              <th className="p-4 text-center">النوع</th>
                              <th className="p-4 text-center hidden lg:table-cell">التصنيف</th>
                              <th className="p-4 text-right hidden md:table-cell">الوصف</th>
                              <th className="p-4 text-right hidden sm:table-cell">العقار</th>
                              <th className="p-4 text-center">المبلغ</th>
                              <th className="p-4 text-center">طباعة سند</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                          {sortedTransactions.slice((transactionPage - 1) * PAGE_SIZE, transactionPage * PAGE_SIZE).map(exp => (
                              <tr key={exp.id} className="hover:bg-slate-50">
                                  <td className="p-4 font-mono text-slate-500 text-right">{new Date(exp.date).toLocaleDateString('en-GB')}</td>
                                  <td className="p-4 text-center">
                                      {exp.type === 'Income' 
                                        ? <span className="text-emerald-600 bg-emerald-50 px-2 py-1 rounded text-xs font-bold border border-emerald-200">إيراد</span>
                                        : <span className="text-rose-600 bg-rose-50 px-2 py-1 rounded text-xs font-bold border border-rose-200">مصروف</span>
                                      }
                                  </td>
                                  <td className="p-4">
                                      <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs font-medium">{exp.categoryName || 'بدون تصنيف'}</span>
                                  </td>
                                  <td className="p-4 text-slate-600">{exp.description}</td>
                                  <td className="p-4 text-slate-600 font-medium">{exp.propertyName}</td>
                                  <td className={`p-4 font-black ${exp.type === 'Income' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                      {exp.type === 'Income' ? '+' : '-'}{exp.amount} د.ك
                                  </td>
                                  <td className="p-4 text-center">
                                      <button 
                                          onClick={() => setSelectedTransactionForPrint(exp)}
                                          className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all active:scale-95"
                                          title="طباعة سند الصرف/القبض"
                                      >
                                          <Printer size={16} />
                                      </button>
                                  </td>
                              </tr>
                          ))}
                          {transactions.length === 0 && (
                            <tr>
                                <td colSpan={7} className="p-10 text-center text-slate-500 font-medium">لا توجد إيرادات أو مصروفات مسجلة</td>
                            </tr>
                          )}
                      </tbody>
                  </table>
                </div>
            </div>
            {renderPagination(transactionPage, transactions.length, setTransactionPage, "معاملة")}
        </div>
    );
  };

  const renderEmployees = () => {
    if (isLoading) return (
      <div className="flex flex-col items-center justify-center p-20 text-slate-400">
        <Loader2 className="animate-spin mb-4" size={40} />
        <p className="font-bold">جاري تحميل بيانات الموظفين...</p>
      </div>
    );
    
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm">
                <div>
                    <h2 className="text-2xl font-black text-slate-800">إدارة الموظفين والرواتب</h2>
                    <p className="text-slate-500 text-sm mt-1">
                        {activeEmployeeTab === 'employees' ? `إجمالي الموظفين المسجلين: ${employees.length}` : `إجمالي عمليات الصرف: ${salaries.length}`}
                    </p>
                </div>
                <button 
                    onClick={() => { setSelectedEmployee(null); setShowEmployeeForm(true); }}
                    className="bg-fly-600 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-fly-700 transition-all shadow-lg shadow-fly-600/20 active:scale-95"
                >
                    <Plus size={18} />
                    <span>إضافة موظف جديد</span>
                </button>
            </div>

            <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-100 max-w-sm mb-6">
                <button 
                    onClick={() => setActiveEmployeeTab('employees')} 
                    className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all ${activeEmployeeTab === 'employees' ? 'bg-white shadow-sm text-fly-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    جدول بيانات الموظفين
                </button>
                <button 
                    onClick={() => setActiveEmployeeTab('salaries')} 
                    className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all ${activeEmployeeTab === 'salaries' ? 'bg-white shadow-sm text-fly-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    سجل الرواتب المصروفة
                </button>
            </div>

            {activeEmployeeTab === 'employees' ? (
                <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-slate-100">
                    <div className="overflow-x-auto">
                        <table className="w-full text-right border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 text-slate-500 text-xs font-black uppercase tracking-widest border-b border-slate-100">
                                <th className="p-6">بيانات الموظف</th>
                                <th className="p-6">المسمى الوظيفي</th>
                                <th className="p-6">الراتب الأساسي</th>
                                <th className="p-6 text-center">يوم الاستحقاق</th>
                                <th className="p-6 text-center">الحالة</th>
                                <th className="p-6 text-left">إجراءات</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {employees.slice((employeePage - 1) * PAGE_SIZE, employeePage * PAGE_SIZE).map(emp => (
                                <tr key={emp.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="p-6">
                                        <div className="flex items-center gap-4">
                                            <div className="bg-slate-100 p-3 rounded-2xl text-slate-400">
                                                <Users size={20} />
                                            </div>
                                            <div>
                                                <div className="font-black text-slate-800 text-base">{emp.fullName}</div>
                                                <div className="text-[10px] text-slate-400 font-mono font-bold tracking-wider uppercase mt-0.5">
                                                    #{emp.id} | {emp.civilId || 'CID:لا يوجد'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold">
                                            {emp.position || 'غير محدد'}
                                        </div>
                                    </td>
                                    <td className="p-6 font-black text-slate-900">{Number(emp.baseSalary).toLocaleString()} KWD</td>
                                    <td className="p-6 text-center text-slate-500 text-sm font-bold">
                                         يوم {emp.salaryDate || 'غير محدد'}
                                    </td>
                                    <td className="p-6">
                                        <div className="flex justify-center">
                                            <span className={`px-4 py-1.5 rounded-full text-[10px] font-black border tracking-wider ${
                                                emp.status === 'Active' ? 'bg-green-50 text-green-600 border-green-100' : 
                                                emp.status === 'On Leave' ? 'bg-blue-50 text-blue-600 border-blue-100' : 
                                                'bg-slate-50 text-slate-400 border-slate-100'
                                            }`}>
                                                {emp.status === 'Active' ? 'على رأس العمل' : emp.status === 'On Leave' ? 'في إجازة' : 'مستقيل'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => { setSelectedEmployee(emp); setShowEmployeeForm(true); }}
                                                className="bg-slate-100 text-slate-600 px-3 py-2 rounded-xl hover:bg-slate-200 transition-all flex items-center justify-center font-bold text-xs" 
                                                title="تعديل الموظف"
                                            >
                                                <Edit size={14} />
                                            </button>
                                            <button 
                                                onClick={async () => {
                                                    const amount = await window.prompt(`صرف راتب لـ ${emp.fullName}`, String(emp.baseSalary));
                                                    if (amount && !isNaN(Number(amount))) {
                                                        const period = new Date().toISOString().substring(0, 7);
                                                        handleSalarySubmit({
                                                            employeeId: emp.id,
                                                            amount: Number(amount),
                                                            paymentDate: new Date().toISOString().split('T')[0],
                                                            period: period,
                                                            status: 'Paid'
                                                        });
                                                    }
                                                }}
                                                className="bg-slate-900 text-white px-4 py-2 rounded-xl hover:bg-slate-800 transition-all flex items-center gap-2 font-bold text-xs shadow-md shadow-slate-900/10 active:scale-95" 
                                                title="تسجيل صرف راتب"
                                            >
                                                <Wallet size={14} />
                                                <span>صرف</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {employees.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="p-20 text-center text-slate-400">
                                        <div className="flex flex-col items-center gap-3">
                                            <Users size={48} className="opacity-20" />
                                            <p className="font-bold">لا يوجد موظفين مسجلين حالياً</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                  </div>
                    {renderPagination(employeePage, employees.length, setEmployeePage, "موظف")}
                </div>
            ) : (
                <div className="bg-slate-900 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-fly-500/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-white/10 p-2.5 rounded-xl">
                                <Calendar className="text-fly-400" size={24} />
                            </div>
                            <h3 className="text-xl font-black">سجل الرواتب المصروفة</h3>
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full text-right text-sm">
                                <thead className="text-slate-400 font-bold border-b border-white/5 uppercase tracking-wider">
                                    <tr>
                                        <th className="py-4 px-4 text-right">اسم الموظف</th>
                                        <th className="py-4 px-4 text-right">الفترة</th>
                                        <th className="py-4 px-4 text-right">المبلغ</th>
                                        <th className="py-4 px-4 text-right">تاريخ الصرف</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {salaries.slice((salaryPage - 1) * PAGE_SIZE, salaryPage * PAGE_SIZE).map(sal => (
                                        <tr key={sal.id} className="hover:bg-white/5 transition-colors">
                                            <td className="py-4 px-2 font-bold">{sal.employeeName}</td>
                                            <td className="py-4 px-2 font-mono text-xs text-fly-400 font-bold uppercase">{sal.period}</td>
                                            <td className="py-4 px-2 font-black text-lg">{Number(sal.amount).toLocaleString()} <span className="text-[10px] text-slate-500 font-bold">KWD</span></td>
                                            <td className="py-4 px-2 text-slate-400">{sal.paymentDate ? String(sal.paymentDate).split('T')[0] : '-'}</td>
                                        </tr>
                                    ))}
                                    {salaries.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="py-10 text-center text-slate-500 font-bold">لم يتم تسجيل عمليات صرف رواتب بعد</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {renderPagination(salaryPage, salaries.length, setSalaryPage, "عملية صرف", 'dark')}
                        </div>
                    </div>
                </div>
            )}

            {showEmployeeForm && (
                <EmployeeForm 
                    initialData={selectedEmployee}
                    onClose={() => { setShowEmployeeForm(false); setSelectedEmployee(null); }}
                    onSave={handleEmployeeSubmit}
                />
            )}
        </div>
    );
  };

  const renderMaintenance = () => <MaintenanceLayout />;

  const _unused_renderMaintenance = () => {
    if (isLoading) return renderLoading();
    return (
      <div className="space-y-6">
         {renderHeader('طلبات الصيانة', 'طلب صيانة جديد', () => setModalType('maintenance'))}
         <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
             <table className="w-full text-right text-sm">
                 <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                     <tr>
                         <th className="p-5">التاريخ</th>
                         <th className="p-5">العقار / الوحدة</th>
                         <th className="p-5">الوصف</th>
                         <th className="p-5">الأولوية</th>
                         <th className="p-5">الحالة</th>
                     </tr>
                 </thead>
                 <tbody className="divide-y divide-slate-100">
                     {maintenance.map((req) => (
                         <tr key={req.id} className="hover:bg-slate-50 transition-colors">
                             <td className="p-5 font-mono text-slate-500">{req.dateReported}</td>
                             <td className="p-5 font-medium text-slate-800">
                                {req.propertyName}
                                <span className="block text-xs text-slate-400 mt-1">{req.unitNumber ? `Unit: ${req.unitNumber}` : 'General'}</span>
                             </td>
                             <td className="p-5 text-slate-600">{req.description}</td>
                             <td className="p-5">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
                                    req.priority === 'High' ? 'bg-red-50 text-red-700 border-red-100' : 
                                    req.priority === 'Medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' : 'bg-blue-50 text-blue-700 border-blue-100'
                                }`}>
                                    {req.priority === 'High' ? 'عالية' : req.priority === 'Medium' ? 'متوسطة' : 'منخفضة'}
                                </span>
                             </td>
                             <td className="p-5">
                                 <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
                                     req.status === 'Completed' ? 'bg-green-50 text-green-700 border-green-100' : 
                                     req.status === 'In Progress' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-yellow-50 text-yellow-700 border-yellow-100'
                                 }`}>
                                     {req.status === 'Completed' ? 'مكتمل' : req.status === 'In Progress' ? 'جاري العمل' : 'قيد الانتظار'}
                                 </span>
                             </td>
                         </tr>
                     ))}
                 </tbody>
             </table>
             {renderPagination(maintenancePage, maintenance.length, setMaintenancePage, "طلب")}
         </div>
      </div>
    );
  };

  const renderUserManagement = () => {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
          {renderHeader('إدارة المستخدمين والصلاحيات', 'إضافة مستخدم جديد', () => {
              setSelectedUser(null);
              setShowUserForm(true);
          })}

          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden overflow-x-auto">
              <table className="w-full text-right min-w-[800px]">
                  <thead className="bg-slate-50 border-b border-slate-100 text-slate-500 font-bold text-xs uppercase tracking-widest">
                      <tr>
                          <th className="p-6">المستخدم</th>
                          <th className="p-6">إسم الموظف</th>
                          <th className="p-6">الصلاحيات الممنوحة</th>
                          <th className="p-6 text-center">الحالة</th>
                          <th className="p-6 text-center">الإجراءات</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 text-sm">
                      {users.map(u => (
                          <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                              <td className="p-6">
                                  <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-xl bg-fly-50 text-fly-600 flex items-center justify-center font-bold">
                                          {u.username ? u.username[0].toUpperCase() : 'U'}
                                      </div>
                                      <span className="font-bold text-slate-800">{u.username}</span>
                                  </div>
                              </td>
                              <td className="p-6 font-medium text-slate-600">{u.fullName}</td>
                              <td className="p-6">
                                  <div className="flex flex-wrap gap-1 max-w-sm">
                                      {Object.entries(u.permissions || {}).filter(([_, v]) => v).map(([k, _]) => (
                                          <span key={k} className="bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap">
                                              {k === 'userManagement' ? 'أدمن' : 
                                               k === 'dashboard' ? 'الرئيسية' :
                                               k === 'properties' ? 'العقارات' :
                                               k === 'tenants' ? 'المستأجرين' :
                                               k === 'invoices' ? 'الفواتير' :
                                               k === 'maintenance' ? 'الصيانة' :
                                               k === 'employees' ? 'الموظفين' : k}
                                          </span>
                                      ))}
                                      {(!u.permissions || Object.values(u.permissions).every(v => !v)) && (
                                          <span className="text-slate-400 italic text-[10px]">لا توجد صلاحيات</span>
                                      )}
                                  </div>
                              </td>
                              <td className="p-6 text-center">
                                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black ${
                                      u.status === 'Active' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                                  }`}>
                                      <div className={`w-1.5 h-1.5 rounded-full ${u.status === 'Active' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                      {u.status === 'Active' ? 'نشط' : 'معطل'}
                                  </div>
                              </td>
                              <td className="p-6">
                                  <div className="flex justify-center gap-2">
                                      <button 
                                          onClick={() => handleToggleUserStatus(u)}
                                          className={`p-2.5 rounded-xl transition-all active:scale-95 ${
                                              u.status === 'Active' ? 'bg-rose-50 text-rose-500 hover:bg-rose-100' : 'bg-emerald-50 text-emerald-500 hover:bg-emerald-100'
                                          }`}
                                          title={u.status === 'Active' ? 'تعطيل الحساب' : 'تنشيط الحساب'}
                                      >
                                          {u.status === 'Active' ? <Lock size={16} /> : <Unlock size={16} />}
                                      </button>
                                      <button 
                                          onClick={() => { setSelectedUser(u); setShowUserForm(true); }}
                                          className="p-2.5 bg-slate-100 text-slate-500 rounded-xl hover:bg-fly-100 hover:text-fly-600 transition-all active:scale-95"
                                          title="تعديل المستخدم"
                                      >
                                          <Edit size={16} />
                                      </button>
                                      {u.id !== currentUser?.id && (
                                          <button 
                                              onClick={() => handleDeleteUser(u.id)}
                                              className="p-2.5 bg-slate-100 text-slate-400 rounded-xl hover:bg-red-50 hover:text-red-500 transition-all active:scale-95"
                                              title="حذف المستخدم"
                                          >
                                              <Trash2 size={16} />
                                          </button>
                                      )}
                                  </div>
                              </td>
                          </tr>
                      ))}
                  </tbody>
              </table>
          </div>
      </div>
    );
  };

  if (isLicensed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fly-600"></div>
      </div>
    );
  }

  if (isLicensed === false) {
    return <ActivationScreen onActivated={() => window.location.reload()} />;
  }

  if (!isAuthenticated) {
    return <LoginForm onLogin={(u, p) => handleLogin(u, p)} />;
  }

  return (
    <div className="flex bg-slate-50 min-h-screen font-sans text-slate-800" dir="rtl">
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={(tab) => {
            setActiveTab(tab);
            setViewMode('list');
            setSelectedPropertyId(null);
            setSelectedTenant(null);
        }} 
        onLogout={handleLogout}
        permissions={currentUser?.permissions}
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={(v) => {
            setIsSidebarCollapsed(v);
            localStorage.setItem('sidebarCollapsed', String(v));
        }}
      />
      
      <main className="flex-1 p-4 md:p-8 overflow-y-auto h-screen scroll-smooth bg-slate-50 relative custom-scrollbar">
        {/* Top Header */}
        <div className="flex flex-wrap justify-between items-center mb-6 md:mb-8 gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
                <button 
                  onClick={() => setIsSidebarCollapsed(false)}
                  className="p-2 bg-slate-900 text-white rounded-lg md:hidden"
                >
                  <Menu size={20} />
                </button>
                <h2 className="text-lg sm:text-xl md:text-2xl font-black text-slate-800 truncate max-w-[50vw]">
                {activeTab === 'dashboard' ? 'لوحة التحكم' : 
                 activeTab === 'properties' ? 'إدارة العقارات والوحدات' :
                 activeTab === 'tenants' ? 'سجل المستأجرين' :
                 activeTab === 'invoices' ? 'الفواتير والتحصيل' :
                 activeTab === 'expenses' ? 'سجل الإيرادات والمصروفات' :
                 activeTab === 'maintenance' ? 'طلبات الصيانة' :
                 activeTab === 'employees' ? 'شؤون الموظفين' :
                 activeTab === 'reports' ? 'التقارير المالية' :
                 activeTab === 'userManagement' ? 'إدارة المستخدمين' :
                 activeTab === 'settings' ? 'إعدادات النظام' : ''}
            </h2>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
                <button 
                    onClick={() => setShowNotifications(true)}
                    className="p-2 sm:p-3 bg-white rounded-xl shadow-sm border border-slate-200 text-slate-400 hover:text-fly-600 hover:bg-fly-50 transition-all relative group active:scale-95"
                >
                    <Bell size={18} />
                    {/* Badge */}
                    <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
                </button>
                <div className="hidden sm:flex items-center gap-3 bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200">
                    <div className="bg-fly-100 p-2 rounded-lg text-fly-600">
                       <ShieldCheck size={18} />
                    </div>
                    <div>
                        <div className="text-xs font-bold text-slate-800">{currentUser?.name || currentUser?.fullName || currentUser?.username || 'مدير النظام'}</div>
                        <div className="text-[10px] text-slate-400">@ {currentUser?.username || 'admin'}</div>
                    </div>
                </div>
            </div>
        </div>

        <div className="w-full pb-10">
            {activeTab === 'dashboard' && <Dashboard 
                properties={properties} 
                tenants={tenants} 
                invoices={invoices} 
                maintenance={maintenance} 
                transactions={transactions} 
                onNavigate={(tab) => setActiveTab(tab)}
            />}
            {activeTab === 'properties' && renderProperties()}
            {activeTab === 'tenants' && renderTenants()}
            {activeTab === 'invoices' && renderInvoices()}
            {activeTab === 'expenses' && renderTransactions()}
            {activeTab === 'maintenance' && renderMaintenance()}
            {activeTab === 'reports' && <ReportsDashboard 
                properties={properties} 
                tenants={tenants} 
                invoices={invoices} 
                transactions={transactions} 
                maintenance={maintenance}
                employees={employees}
                salaries={salaries}
            />}
            {activeTab === 'employees' && renderEmployees()}
            {activeTab === 'userManagement' && renderUserManagement()}
            {activeTab === 'settings' && <Settings 
                companyInfo={companyInfo}
                onCompanyInfoUpdate={setCompanyInfo}
            />}
        </div>
      </main>

      {/* Notifications Panel */}
      {showNotifications && (
          <div className="fixed inset-0 z-50 flex items-start justify-start">
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowNotifications(false)}></div>
              <Notifications onClose={() => setShowNotifications(false)} />
          </div>
      )}

      {/* Modals */}
      {modalType === 'maintenance' && (
          <MaintenanceForm onCancel={() => setModalType('none')} onSubmit={handleMaintenanceSubmit} />
      )}
      
      {showInvoiceForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
            <div className="w-full max-w-2xl">
                <InvoiceForm onCancel={() => setShowInvoiceForm(false)} onSubmit={handleInvoiceSubmit} />
            </div>
        </div>
      )}

      {showUserForm && (
        <UserForm 
            initialData={selectedUser} 
            onClose={() => setShowUserForm(false)} 
            onSave={handleUserSubmit} 
        />
      )}

      {selectedInvoiceForPayment && (
        <PaymentDialog 
            invoice={selectedInvoiceForPayment} 
            onClose={() => setSelectedInvoiceForPayment(null)} 
            onConfirm={async (data) => {
                await handlePayInvoice(selectedInvoiceForPayment.id, {
                    payment_id: data.receiptNumber,
                    payment_method: data.method,
                    payment_reference: data.reference,
                    payment_date: data.paymentDate,
                    service_charge: data.serviceFee || 0
                });
            }} 
        />
      )}

      {selectedInvoiceForReceipt && (
        <InvoiceReceipt 
            invoice={selectedInvoiceForReceipt} 
            companyInfo={companyInfo}
            onClose={() => setSelectedInvoiceForReceipt(null)} 
        />
      )}

      {selectedInvoiceForDetails && (
        <CollectionNotice 
            invoice={selectedInvoiceForDetails} 
            companyInfo={companyInfo}
            linkedExpenses={transactions.filter(e => e.invoiceId === selectedInvoiceForDetails.id && e.type === 'Expense')}
            onClose={() => setSelectedInvoiceForDetails(null)} 
        />
      )}
      {selectedTransactionForPrint && (
        <TransactionReceipt 
            transaction={selectedTransactionForPrint} 
            companyInfo={companyInfo} 
            onClose={() => setSelectedTransactionForPrint(null)} 
        />
      )}
      {selectedTenantForDetails && (
        <TenantDetailsDialog 
            tenant={selectedTenantForDetails} 
            onClose={() => setSelectedTenantForDetails(null)} 
        />
      )}
      {showCategoryManager && (
        <CategoryManagerDialog 
            categories={transactionCategories} 
            setCategories={setTransactionCategories} 
            onClose={() => setShowCategoryManager(false)} 
        />
      )}

      {customAlert.visible && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200" dir="rtl">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 flex flex-col items-center text-center space-y-4 border border-slate-100 transform scale-100 transition-all duration-300 animate-in zoom-in-95">
            <div className={`p-4 rounded-2xl ${
              customAlert.message.includes('❌') || customAlert.message.includes('فشل') || customAlert.message.includes('تعذر') || customAlert.message.includes('خطأ')
                ? 'bg-rose-50 text-rose-600'
                : customAlert.message.includes('⚠️')
                  ? 'bg-amber-50 text-amber-600'
                  : 'bg-emerald-50 text-emerald-600'
            }`}>
              {customAlert.message.includes('❌') || customAlert.message.includes('فشل') || customAlert.message.includes('تعذر') || customAlert.message.includes('خطأ') ? (
                <XCircle size={32} />
              ) : customAlert.message.includes('⚠️') ? (
                <AlertTriangle size={32} />
              ) : (
                <CheckCircle2 size={32} />
              )}
            </div>
            <h3 className="text-lg font-black text-slate-800">تنبيه النظام</h3>
            <p className="text-sm font-bold text-slate-600 leading-relaxed">
              {customAlert.message.replace(/[❌⚠️✅]/g, '').trim()}
            </p>
            <button
              onClick={() => setCustomAlert({ message: '', visible: false })}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-xl font-black text-sm transition-all active:scale-95 shadow-lg shadow-slate-900/10"
            >
              موافق
            </button>
          </div>
        </div>
      )}

      {customConfirm.visible && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200" dir="rtl">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 flex flex-col items-center text-center space-y-4 border border-slate-100 transform scale-100 transition-all duration-300 animate-in zoom-in-95">
            <div className="p-4 rounded-2xl bg-amber-50 text-amber-600">
              <AlertTriangle size={32} />
            </div>
            <h3 className="text-lg font-black text-slate-800">تأكيد الإجراء</h3>
            <p className="text-sm font-bold text-slate-600 leading-relaxed">
              {customConfirm.message.replace(/[⚠️]/g, '').trim()}
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => customConfirm.resolve(true)}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-xl font-black text-sm transition-all active:scale-95 shadow-lg shadow-slate-900/10"
              >
                تأكيد
              </button>
              <button
                onClick={() => customConfirm.resolve(false)}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-xl font-black text-sm transition-all active:scale-95 border border-slate-200"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {customPrompt.visible && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200" dir="rtl">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 flex flex-col items-center text-center space-y-4 border border-slate-100 transform scale-100 transition-all duration-300 animate-in zoom-in-95">
            <div className="p-4 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
              <Wallet size={32} />
            </div>
            <h3 className="text-lg font-black text-slate-800">إدخال مطلوب</h3>
            <p className="text-sm font-bold text-slate-600 leading-relaxed">
              {customPrompt.message}
            </p>
            <input
              type="text"
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all font-bold text-sm shadow-sm text-center"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  customPrompt.resolve(promptValue);
                }
              }}
            />
            <div className="flex gap-3 w-full">
              <button
                onClick={() => customPrompt.resolve(promptValue)}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-xl font-black text-sm transition-all active:scale-95 shadow-lg shadow-slate-900/10"
              >
                حفظ
              </button>
              <button
                onClick={() => customPrompt.resolve(null)}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-xl font-black text-sm transition-all active:scale-95 border border-slate-200"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;